/**
 * src/three/pixelFont.js
 * ตัวเลขพิกเซล + ข้อความหัวร้อน (มีพื้นหลังทึบ)
 */
import * as THREE from 'three';

const GLYPHS = {
  0: ['111', '101', '101', '101', '111'],
  1: ['010', '110', '010', '010', '111'],
  2: ['111', '001', '111', '100', '111'],
  3: ['111', '001', '111', '001', '111'],
  4: ['101', '101', '111', '001', '001'],
  5: ['111', '100', '111', '001', '111'],
  6: ['111', '100', '111', '101', '111'],
  7: ['111', '001', '010', '010', '010'],
  8: ['111', '101', '111', '101', '111'],
  9: ['111', '101', '111', '001', '111'],
};

export function drawPixelNumber(value, opts = {}) {
  const scale = opts.scale ?? 10;
  const pad = opts.pad ?? 4;
  const gap = opts.gap ?? 2;
  const str = String(value);
  const gw = 3;
  const gh = 5;
  const digitW = gw * scale;
  const digitH = gh * scale;
  const w = pad * 2 + str.length * digitW + (str.length - 1) * gap * scale;
  const h = pad * 2 + digitH;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, w, h);

  ctx.fillStyle = '#0a0606';
  for (let di = 0; di < str.length; di++) {
    const g = GLYPHS[str[di]];
    if (!g) continue;
    const ox = pad + di * (digitW + gap * scale) + scale * 0.35;
    const oy = pad + scale * 0.35;
    for (let row = 0; row < gh; row++) {
      for (let col = 0; col < gw; col++) {
        if (g[row][col] === '1') ctx.fillRect(ox + col * scale, oy + row * scale, scale, scale);
      }
    }
  }

  ctx.fillStyle = '#ffffff';
  for (let di = 0; di < str.length; di++) {
    const g = GLYPHS[str[di]];
    if (!g) continue;
    const ox = pad + di * (digitW + gap * scale);
    const oy = pad;
    for (let row = 0; row < gh; row++) {
      for (let col = 0; col < gw; col++) {
        if (g[row][col] === '1') ctx.fillRect(ox + col * scale, oy + row * scale, scale, scale);
      }
    }
  }

  return { canvas, width: w, height: h };
}

export function createPixelNumberSprite(value, opts = {}) {
  const { canvas, width, height } = drawPixelNumber(value, opts);
  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;

  const mat = new THREE.SpriteMaterial({
    map: tex,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    fog: false,
  });
  const sprite = new THREE.Sprite(mat);
  const worldH = opts.worldHeight ?? 0.85;
  sprite.scale.set(worldH * (width / height), worldH, 1);
  sprite.renderOrder = 30;
  return sprite;
}

/** ป้ายข้อความหัวร้อน — stamp ขอบดำ เงาember ไม่มีกล่องแดง */
export function createRageWordSprite(phrase, opts = {}) {
  const text = String(phrase || 'อัด!');
  const fontSize = opts.fontSize ?? 128;
  const padX = 56;
  const padY = 48;
  const font = `700 ${fontSize}px "Kanit", Tahoma, sans-serif`;

  const measure = document.createElement('canvas').getContext('2d');
  measure.font = font;
  let tw = Math.ceil(measure.measureText(text).width);
  if (!tw || tw < fontSize * 0.5) {
    tw = Math.ceil(fontSize * Math.max(2, text.length) * 0.9);
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(tw + padX * 2 + 24);
  canvas.height = Math.ceil(fontSize * 1.35 + padY * 2);
  const ctx = canvas.getContext('2d');

  const cx = canvas.width / 2 - 6;
  const cy = canvas.height / 2 - 4;
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // เงา ember ใต้ตัวอักษร
  ctx.fillStyle = 'rgba(196, 34, 0, 0.9)';
  ctx.fillText(text, cx + 8, cy + 10);
  ctx.fillStyle = 'rgba(80, 10, 0, 0.45)';
  ctx.fillText(text, cx + 12, cy + 16);

  // ขอบดำหนา
  ctx.lineJoin = 'round';
  ctx.miterLimit = 2;
  ctx.lineWidth = Math.max(16, fontSize * 0.14);
  ctx.strokeStyle = '#120806';
  ctx.strokeText(text, cx, cy);

  // ไฮไลต์ขอบอุ่นบางชั้น
  ctx.lineWidth = Math.max(6, fontSize * 0.05);
  ctx.strokeStyle = '#5a1808';
  ctx.strokeText(text, cx, cy);

  // ตัวอักษรครีม
  ctx.fillStyle = '#fff6e8';
  ctx.fillText(text, cx, cy);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;

  const mat = new THREE.SpriteMaterial({
    map: tex,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    fog: false,
  });
  const sprite = new THREE.Sprite(mat);
  const worldH = opts.worldHeight ?? 1.8;
  sprite.scale.set(worldH * (canvas.width / canvas.height), worldH, 1);
  sprite.renderOrder = 50;
  return sprite;
}
